import {
  createFileRoute,
  Outlet,
  useNavigate,
  useParams,
  useRouterState,
} from '@tanstack/react-router';
import { SidebarInset, SidebarProvider } from '@repo/ui/components/sidebar';
import { SpaceStoreProvider, spaceParams } from '@/state/space.store.tsx';
import { useMutation, useQuery } from 'convex/react';
import { api } from '@repo/backend/convex/_generated/api';
import { NotFoundComponent } from '@/components/NotFoundComponent.tsx';
import { LoadingPlaceholder } from '@components/loading-placeholder.tsx';
import { FileDropUploadZone } from '@/components/files/FileDropUploadZone';
import { ErrorBoundary } from '@/components/ErrorBoundary.tsx';
import { AllModals } from '@/components/AllModals.tsx';
import { DocumentPreviewSheet } from '@/components/documents/DocumentPreviewSheet.tsx';
import { useEffect, type CSSProperties } from 'react';
import { LegacySpaceUrlRedirect } from '@/components/spaces/LegacySpaceUrlRedirect';
import { AppRail } from '@/components/navigation/AppRail';
import { ContextPanel } from '@/components/navigation/ContextPanel';
import { SpaceHeader } from '@/components/navigation/SpaceHeader';
import { MobileTabBar } from '@/components/navigation/MobileTabBar';

export const Route = createFileRoute('/$spaceSlug')({
  component: RouteComponent,
  notFoundComponent: NotFoundComponent,
});

function RouteComponent() {
  const { spaceSlug } = useParams({ from: '/$spaceSlug' });
  const href = useRouterState({ select: (s) => s.location.href });
  const navigate = useNavigate();

  const space = useQuery(api.spaceFunctions.getUserSpace, { spaceSlug });

  const recordSpaceAccess = useMutation(api.spaceFunctions.recordSpaceAccess);

  // Only record access once the space has resolved; the mutation rejects for
  // unknown or inaccessible spaces.
  const resolvedSpaceId = space?._id;
  useEffect(() => {
    if (resolvedSpaceId) {
      recordSpaceAccess({ spaceId: resolvedSpaceId });
    }
  }, [resolvedSpaceId, recordSpaceAccess]);

  if (space === undefined) {
    return <LoadingPlaceholder text="Finding your space..." />;
  }

  if (space === null) {
    return <LegacySpaceUrlRedirect spaceSlug={spaceSlug} />;
  }

  return (
    <SpaceStoreProvider space={space}>
      <FileDropUploadZone
        spaceId={space._id}
        onUploaded={() =>
          navigate({
            to: '/$spaceSlug/library',
            params: spaceParams(space),
            search: { kind: 'upload' },
          })
        }
      >
        <div className="flex h-svh overflow-hidden">
          <AppRail />
          <SidebarProvider
            className="h-full min-h-0 min-w-0 flex-1 flex-col"
            style={{ '--sidebar-width': '17rem' } as CSSProperties}
          >
            <SpaceHeader />
            <div className="flex min-h-0 flex-1">
              <ContextPanel />
              <SidebarInset className="h-full min-w-0 overflow-hidden">
                <div className="flex flex-1 flex-col overflow-hidden">
                  <ErrorBoundary resetKey={href || '/'}>
                    <Outlet />
                  </ErrorBoundary>
                </div>
                <MobileTabBar />
              </SidebarInset>
            </div>
          </SidebarProvider>
        </div>
      </FileDropUploadZone>
      <AllModals />
      <DocumentPreviewSheet />
    </SpaceStoreProvider>
  );
}
